import { useCallback, useEffect, useRef, useState } from 'react';
import {
  amplitudeToLevel,
  nativeApp,
  readNativeFile,
  type NativeRecordingState,
  type TermDeckAppPlugin,
} from '../lib/native-app';
import type { AudioRecorderOptions, AudioRecorderApi, RecoverableRecording } from './useAudioRecorder';
import { friendlyTranscribeError } from './useAudioRecorder';

const BATTERY_ASKED_KEY = 'td_native_battery_asked';

/**
 * ECO.18 — gravação pelo app Android: o MESMO contrato do `useAudioRecorder` web (os consumidores não mudam), com a
 * captura no serviço nativo de primeiro plano. A diferença que o dono sente: pode bloquear a tela ou trocar de app,
 * e a gravação continua; se o texto não voltar (rede, app fechado, transcrição falha), o áudio fica guardado no
 * aparelho e reaparece como recuperável. Parar pela notificação transcreve sozinho quando o app volta à frente.
 */
export function useNativeAudioRecorder(
  transcribe: (blob: Blob, filename: string) => Promise<string>,
  recordingKey: string,
  options: AudioRecorderOptions = {},
): AudioRecorderApi {
  const plugin = nativeApp() as TermDeckAppPlugin;
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recoverable, setRecoverable] = useState<RecoverableRecording | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const stoppingRef = useRef(false);
  const onBackgroundRef = useRef(options.onBackgroundTranscript);
  onBackgroundRef.current = options.onBackgroundTranscript;
  const transcribeRef = useRef(transcribe);
  transcribeRef.current = transcribe;

  const transcribeStored = useCallback(async (id: string, startedAt: number): Promise<string | null> => {
    setTranscribing(true);
    const began = Date.now();
    setNotice('transcrevendo…');
    const timer = setInterval(() => setNotice(`transcrevendo… (${Math.round((Date.now() - began) / 1000)}s)`), 1000);
    try {
      const blob = await readNativeFile(plugin, 'recording', id, 'audio/aac');
      if (blob.size === 0) {
        await plugin.finishRecording({ id });
        setRecoverable(null);
        setNotice('a gravação ficou vazia');
        return null;
      }
      const text = await transcribeRef.current(blob, 'gravacao.aac');
      await plugin.finishRecording({ id });
      setRecoverable(null);
      setNotice(null);
      return text;
    } catch (err) {
      // O áudio NÃO sai do aparelho: fica recuperável até dar certo ou o dono descartar.
      setRecoverable({ recordingId: id, startedAt, reason: 'failed' });
      setNotice(friendlyTranscribeError(err));
      return null;
    } finally {
      clearInterval(timer);
      setTranscribing(false);
    }
  }, [plugin]);

  // Estado do serviço: gravação que continua de antes (WebView recriado) e gravações guardadas no aparelho.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const status = await plugin.recordingStatus();
        if (!alive) return;
        if (status.active && status.key === recordingKey && status.id) {
          activeIdRef.current = status.id;
          setRecording(true);
        }
        const { recordings } = await plugin.listRecordings();
        const mine = recordings.filter(r => r.key === recordingKey && r.id !== status.id);
        if (alive && mine.length) setRecoverable({ recordingId: mine[0]!.id, startedAt: mine[0]!.startedAt, reason: 'device' });
      } catch { /* sem estado nativo legível: o botão segue funcionando */ }
    })();
    return () => { alive = false; };
  }, [plugin, recordingKey]);

  // Medidor e fim pela notificação: enquanto grava, lê o serviço a cada 200 ms.
  useEffect(() => {
    if (!recording) { setAudioLevel(0); return; }
    let alive = true;
    const tick = async (): Promise<void> => {
      try {
        const status: NativeRecordingState = await plugin.recordingStatus();
        if (!alive) return;
        if (status.active) { setAudioLevel(amplitudeToLevel(status.amplitude)); return; }
        if (stoppingRef.current) return;
        // Parou fora do app (botão Parar da notificação ou erro do gravador): transcreve agora.
        const id = activeIdRef.current;
        activeIdRef.current = null;
        setRecording(false);
        if (status.error) setNotice(`a gravação parou: ${status.error}`);
        if (!id) return;
        const text = await transcribeStored(id, Date.now());
        if (text && onBackgroundRef.current) onBackgroundRef.current(text);
        else if (text) setRecoverable(null);
      } catch { /* leitura perdida: tenta no próximo tique */ }
    };
    const timer = setInterval(() => void tick(), 200);
    return () => { alive = false; clearInterval(timer); };
  }, [plugin, recording, transcribeStored]);

  const start = async (): Promise<void> => {
    setNotice(null);
    try {
      const state = await plugin.startRecording({ key: recordingKey });
      activeIdRef.current = state.id;
      stoppingRef.current = false;
      setRecoverable(null);
      setRecording(true);
      // Uma vez: tirar o app da economia de bateria, senão alguns aparelhos matam o serviço com a tela apagada.
      try {
        if (!localStorage.getItem(BATTERY_ASKED_KEY)) {
          localStorage.setItem(BATTERY_ASKED_KEY, String(Date.now()));
          const info = await plugin.info();
          if (!info.batteryExempt) void plugin.requestBatteryExemption();
        }
      } catch { /* opcional */ }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setNotice(message.includes('negada') ? message : `não foi possível gravar: ${message}`);
    }
  };

  const stop = async (): Promise<string | null> => {
    if (!recording) return null;
    stoppingRef.current = true;
    const startedAt = Date.now();
    try {
      const result = await plugin.stopRecording();
      setRecording(false);
      const id = result.id ?? activeIdRef.current;
      activeIdRef.current = null;
      if (!id) return null;
      return await transcribeStored(id, result.startedAt ?? startedAt);
    } finally {
      stoppingRef.current = false;
    }
  };

  /** ECO4.8-S — cancelar: para e descarta o áudio no aparelho, sem transcrever. */
  const cancel = async (): Promise<void> => {
    if (!recording) return;
    stoppingRef.current = true;
    try {
      const result = await plugin.stopRecording();
      setRecording(false);
      const id = result.id ?? activeIdRef.current;
      activeIdRef.current = null;
      if (id) { try { await plugin.finishRecording({ id }); } catch { /* já não existe */ } }
      setNotice('gravação descartada');
    } finally {
      stoppingRef.current = false;
    }
  };

  const recover = useCallback(async (): Promise<string | null> => {
    const rec = recoverable;
    if (!rec) return null;
    return transcribeStored(rec.recordingId, rec.startedAt);
  }, [recoverable, transcribeStored]);

  const discardRecoverable = useCallback(async (): Promise<void> => {
    const rec = recoverable;
    if (!rec) return;
    try { await plugin.finishRecording({ id: rec.recordingId }); } catch { /* já não existe */ }
    setRecoverable(null);
    setNotice(null);
  }, [plugin, recoverable]);

  return {
    recording,
    transcribing,
    notice,
    audioLevel,
    recoverable,
    background: true,
    start,
    stop,
    cancel,
    recover,
    discardRecoverable,
    clearNotice: () => setNotice(null),
  };
}
